/**
 * SugarFur Theme - Main JavaScript
 * Modern, performant, accessible
 */

(function() {
  'use strict';

  window.SugarFur = window.SugarFur || {};

  // Utility Functions
  const Utils = {
    debounce(fn, delay = 300) {
      let timeoutId;
      return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    throttle(fn, limit = 100) {
      let inThrottle;
      let trailingArgs = null;
      return function(...args) {
        if (!inThrottle) {
          fn.apply(this, args);
          inThrottle = true;
          setTimeout(() => {
            inThrottle = false;
            // Trailing edge: run once more with the last dropped args so final state is correct
            if (trailingArgs) {
              const lastArgs = trailingArgs;
              trailingArgs = null;
              fn.apply(this, lastArgs);
            }
          }, limit);
        } else {
          trailingArgs = args;
        }
      };
    },

    formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const value = (cents / 100).toFixed(2);
      const formatString = format || window.theme?.moneyFormat || '${{amount}}';
      return formatString
        .replace(/\{\{\s*amount\s*\}\}/, value)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
    },

    async fetchJSON(url, options = {}) {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        }
      });
      if (!response.ok) {
        // Surface Shopify's human-readable error (e.g. stock limits) instead of a raw status code
        let message = `HTTP error! status: ${response.status}`;
        try {
          const data = await response.json();
          message = data.description || data.message || message;
        } catch (e) { /* non-JSON error body */ }
        throw new Error(message);
      }
      return response.json();
    },

    async fetchHTML(url) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return response.text();
    },

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },

    escapeAttr(str) {
      // escapeHtml (textContent/innerHTML) does not escape quotes, which breaks attribute values
      return this.escapeHtml(String(str)).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    },

    trapFocus(container, initialFocus) {
      const previouslyFocused = document.activeElement;
      const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const getFocusable = () => Array.from(container.querySelectorAll(focusableSelector))
        .filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
      const target = initialFocus || getFocusable()[0] || container;
      if (target && typeof target.focus === 'function') target.focus();
      const handleKeydown = (e) => {
        if (e.key !== 'Tab') return;
        const focusable = getFocusable();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      container.addEventListener('keydown', handleKeydown);
      return () => {
        container.removeEventListener('keydown', handleKeydown);
        if (previouslyFocused && typeof previouslyFocused.focus === 'function' && document.contains(previouslyFocused)) {
          previouslyFocused.focus();
        }
      };
    }
  };

  SugarFur.Utils = Utils;

  // Header
  const Header = {
    header: null,
    lastScrollY: 0,

    init() {
      this.header = document.querySelector('[data-header]');
      if (!this.header) return;
      window.addEventListener('scroll', Utils.throttle(() => this.handleScroll(), 100));

      // Desktop dropdowns - hover and click support
      const dropdowns = this.header.querySelectorAll('[data-dropdown]');
      for (const dropdown of dropdowns) {
        const toggle = dropdown.querySelector('[data-dropdown-toggle]');

        // Parent items with a real URL are links: click NAVIGATES (the dropdown
        // already opens on hover). On touch screens there is no hover, so the
        // first tap opens the menu and the second follows the link.
        toggle?.addEventListener('click', (e) => {
          const isLink = toggle.tagName === 'A' && toggle.getAttribute('href');
          if (!isLink) {
            e.preventDefault();
            this.toggleDropdown(dropdown);
            return;
          }
          if (window.matchMedia('(hover: none)').matches && !dropdown.classList.contains('is-open')) {
            e.preventDefault();
            this.toggleDropdown(dropdown);
          }
        });

        // Keyboard: ArrowDown opens the menu and moves focus into it
        toggle?.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            dropdown.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
            dropdown.querySelector('[data-dropdown-menu] a')?.focus();
          }
        });

        // Hover support for desktop
        dropdown.addEventListener('mouseenter', () => {
          dropdown.classList.add('is-open');
          toggle?.setAttribute('aria-expanded', 'true');
        });

        dropdown.addEventListener('mouseleave', () => {
          dropdown.classList.remove('is-open');
          toggle?.setAttribute('aria-expanded', 'false');
        });
      }

      // Close on outside click / Escape — one listener for all dropdowns
      if (dropdowns.length) {
        document.addEventListener('click', (e) => {
          for (const dropdown of dropdowns) {
            if (dropdown.classList.contains('is-open') && !dropdown.contains(e.target)) {
              dropdown.classList.remove('is-open');
              dropdown.querySelector('[data-dropdown-toggle]')?.setAttribute('aria-expanded', 'false');
            }
          }
        });
        document.addEventListener('keydown', (e) => {
          if (e.key !== 'Escape') return;
          for (const dropdown of dropdowns) {
            if (dropdown.classList.contains('is-open')) {
              const toggle = dropdown.querySelector('[data-dropdown-toggle]');
              dropdown.classList.remove('is-open');
              toggle?.setAttribute('aria-expanded', 'false');
              if (dropdown.contains(document.activeElement)) toggle?.focus();
            }
          }
        });
      }
    },

    handleScroll() {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 50) {
        this.header.classList.add('header--scrolled');
      } else {
        this.header.classList.remove('header--scrolled');
      }
      this.lastScrollY = currentScrollY;
    },

    toggleDropdown(dropdown) {
      const isOpen = dropdown.classList.contains('is-open');
      const toggle = dropdown.querySelector('[data-dropdown-toggle]');
      for (const d of this.header.querySelectorAll('[data-dropdown]')) {
        d.classList.remove('is-open');
        d.querySelector('[data-dropdown-toggle]')?.setAttribute('aria-expanded', 'false');
      }
      if (!isOpen) {
        dropdown.classList.add('is-open');
        toggle?.setAttribute('aria-expanded', 'true');
      }
    }
  };

  SugarFur.Header = Header;

  // Cart
  const Cart = {
    drawer: null,

    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');

      // Cart toggle — document-level delegation so it survives drawer innerHTML replacement
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (toggle) {
          e.preventDefault();
          this.toggleDrawer();
        }
      });

      // Quick Add buttons — document-level delegation so buttons appended later
      // (Load More, section re-renders) work without re-binding (and can't double-bind)
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn || btn.disabled) return;
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        const originalText = btn.textContent;
        btn.disabled = true;
        this.addItem(variantId, 1).finally(() => {
          btn.disabled = false;
          btn.textContent = originalText;
        });
      });

      document.querySelector('[data-overlay]')?.addEventListener('click', () => {
        if (this.drawer?.classList.contains('is-open')) this.closeDrawer();
      });

      // Close drawer on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
          this.closeDrawer();
        }
      });

      // Cart drawer quantity change and remove buttons (delegated)
      this.drawer?.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-cart-qty-change]');
        if (qtyBtn) {
          e.preventDefault();
          // The buttons carry server-rendered target quantities; a second click before
          // the drawer re-renders would resend the same stale target — lock until refresh.
          if (this._mutating) return;
          const key = qtyBtn.dataset.key;
          const qty = parseInt(qtyBtn.dataset.qty, 10);
          if (!key || Number.isNaN(qty)) return;
          this._mutating = true;
          this.changeItemQuantity(key, qty).finally(() => {
            this._mutating = false;
          });
        }
      });

      // Upsell add-to-cart (delegated on drawer)
      this.drawer?.addEventListener('click', (e) => {
        const upsellBtn = e.target.closest('[data-upsell-add]');
        if (upsellBtn) {
          e.preventDefault();
          const variantId = upsellBtn.dataset.variantId || upsellBtn.dataset.upsellAdd;
          if (variantId && !upsellBtn.disabled) {
            const originalText = upsellBtn.textContent;
            upsellBtn.disabled = true;
            upsellBtn.textContent = '...';
            this.addItem(variantId, 1).finally(() => {
              // If the drawer re-rendered the button is gone; this is a safe no-op then
              upsellBtn.disabled = false;
              upsellBtn.textContent = originalText;
            });
          }
        }
      });

      // ── Drawer note & gift wrap — all delegated so they survive innerHTML replacement ──

      // Order note saving (debounced)
      const saveNote = Utils.debounce((note) => {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note })
        }).then((response) => {
          if (!response.ok) throw new Error('Note save failed');
        }).catch(() => {
          this.showToast(window.theme?.strings?.cartError || 'Could not save your order note. Please try again.');
        });
      }, 500);

      this.drawer?.addEventListener('input', (e) => {
        if (e.target.matches('[data-cart-note]')) {
          saveNote(e.target.value);
        }
      });

      // Note toggle checkbox — show/hide textarea
      this.drawer?.addEventListener('change', (e) => {
        if (e.target.matches('[data-note-toggle]')) {
          const noteBody = this.drawer.querySelector('[data-note-body]');
          if (!noteBody) return;
          if (e.target.checked) {
            noteBody.removeAttribute('hidden');
            noteBody.offsetHeight; // reflow
            noteBody.classList.add('cart-drawer__note-body--visible');
            const textarea = noteBody.querySelector('textarea');
            if (textarea) textarea.focus();
          } else {
            noteBody.classList.remove('cart-drawer__note-body--visible');
            setTimeout(() => noteBody.setAttribute('hidden', ''), 300);
            // Clear note when unchecked
            saveNote('');
          }
        }

        // Gift wrap checkbox — show/hide message, save attribute
        if (e.target.matches('[data-gift-wrap-checkbox]')) {
          const msgWrap = this.drawer.querySelector('[data-gift-wrap-message-wrap]');
          if (msgWrap) {
            if (e.target.checked) {
              msgWrap.removeAttribute('hidden');
              msgWrap.offsetHeight;
              msgWrap.classList.add('cart-gift-wrap__message-wrap--visible');
            } else {
              msgWrap.classList.remove('cart-gift-wrap__message-wrap--visible');
              setTimeout(() => msgWrap.setAttribute('hidden', ''), 300);
            }
          }
          this.saveCartAttributes();
        }
      });

      // Gift message input (debounced)
      const debouncedGiftMsg = Utils.debounce(() => this.saveCartAttributes(), 500);
      this.drawer?.addEventListener('input', (e) => {
        if (e.target.matches('[data-gift-wrap-message]')) {
          debouncedGiftMsg();
        }
      });

      // Listen for cart:refresh events (dispatched by other scripts)
      document.addEventListener('cart:refresh', () => this.updateCartUI());

      // Product-card color swatches — swap the card image to the swatch's variant image
      document.addEventListener('click', (e) => {
        const swatch = e.target.closest('[data-swatch]');
        if (!swatch) return;
        e.preventDefault();
        const card = swatch.closest('.product-card');
        if (!card) return;
        if (swatch.dataset.image) {
          const img = card.querySelector('.product-card__image--primary');
          if (img) {
            img.src = swatch.dataset.image;
            img.removeAttribute('srcset');
          }
        }
        for (const s of card.querySelectorAll('[data-swatch]')) {
          s.classList.remove('is-active');
          s.setAttribute('aria-pressed', 'false');
        }
        swatch.classList.add('is-active');
        swatch.setAttribute('aria-pressed', 'true');
      });

      // ── Cart Page handlers ──
      this.cartPage = document.querySelector('[data-cart-page]');
      if (this.cartPage) {
        this.cartPage.addEventListener('click', (e) => {
          const minusBtn = e.target.closest('[data-quantity-minus]');
          const plusBtn = e.target.closest('[data-quantity-plus]');
          const removeBtn = e.target.closest('[data-remove-item]');

          if (minusBtn) {
            e.preventDefault();
            const key = minusBtn.dataset.key;
            const input = minusBtn.closest('.quantity-selector')?.querySelector('.quantity-selector__input');
            const qty = Math.max(0, (parseInt(input?.value) || 1) - 1);
            if (input) input.value = qty;
            this.cartPageChange(key, qty);
          }

          if (plusBtn) {
            e.preventDefault();
            const key = plusBtn.dataset.key;
            const input = plusBtn.closest('.quantity-selector')?.querySelector('.quantity-selector__input');
            const qty = (parseInt(input?.value) || 0) + 1;
            if (input) input.value = qty;
            this.cartPageChange(key, qty);
          }

          if (removeBtn) {
            e.preventDefault();
            const key = removeBtn.dataset.key;
            const item = removeBtn.closest('[data-cart-item]');
            if (item) item.classList.add('cart-page__item--removing');
            this.cartPageChange(key, 0);
          }
        });

        // Debounced direct input change — one debouncer per line item, so editing
        // item A then item B quickly doesn't cancel A's pending update
        const inputDebouncers = new Map();
        this.cartPage.addEventListener('change', (e) => {
          const input = e.target.closest('.quantity-selector__input');
          if (input) {
            const key = input.closest('[data-cart-item]')?.dataset.key;
            const qty = Math.max(0, parseInt(input.value) || 0);
            if (key) {
              if (!inputDebouncers.has(key)) {
                inputDebouncers.set(key, Utils.debounce((k, q) => this.cartPageChange(k, q), 400));
              }
              inputDebouncers.get(key)(key, qty);
            }
          }
        });
      }
    },

    toggleDrawer() {
      if (this.drawer?.classList.contains('is-open')) {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    },

    openDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.querySelector('[data-overlay]')?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      this._releaseFocus = Utils.trapFocus(
        this.drawer,
        this.drawer.querySelector('.cart-drawer__close, [data-cart-toggle]')
      );
      this.loadUpsells();
    },

    closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.querySelector('[data-overlay]')?.classList.remove('is-visible');
      document.body.style.overflow = '';
      if (this._releaseFocus) {
        this._releaseFocus();
        this._releaseFocus = null;
      }
    },

    async addItem(variantId, quantity = 1, sellingPlan = '') {
      try {
        const body = { id: variantId, quantity: quantity };
        if (sellingPlan) body.selling_plan = sellingPlan;
        const response = await Utils.fetchJSON(window.theme.routes.cart_add_url + '.js', {
          method: 'POST',
          body: JSON.stringify(body)
        });
        await this.updateCartUI();
        this.openDrawer();
        document.dispatchEvent(new CustomEvent('cart:added', { detail: response }));
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showToast(error.message || 'Could not add item to cart. Please try again.');
      }
    },

    showToast(message) {
      const container = document.getElementById('ToastContainer');
      if (!container) return;
      const toast = document.createElement('div');
      toast.className = 'toast toast--error';
      toast.textContent = message;
      container.appendChild(toast);
      setTimeout(() => {
        toast.classList.add('toast--leaving');
        toast.addEventListener('transitionend', () => toast.remove());
      }, 4000);
    },

    saveCartAttributes() {
      const giftCheckbox = this.drawer?.querySelector('[data-gift-wrap-checkbox]');
      const giftMessage = this.drawer?.querySelector('[data-gift-wrap-message]');
      const attributes = {
        gift_wrapping: giftCheckbox?.checked ? 'true' : '',
        gift_message: giftMessage?.value || ''
      };
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      }).catch(err => console.error('Cart attribute update error:', err));
    },

    async changeItemQuantity(key, quantity) {
      try {
        await Utils.fetchJSON(window.theme.routes.cart_change_url + '.js', {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        await this.updateCartUI();
      } catch (error) {
        console.error('Error changing cart quantity:', error);
        this.showToast(error.message || window.theme?.strings?.cartError || 'Unable to update quantity. Please try again.');
        // Re-sync the drawer so the displayed quantity matches the real cart
        await this.updateCartUI();
      }
    },

    async cartPageChange(key, quantity) {
      const page = this.cartPage;
      if (!page) return;
      page.classList.add('is-loading');

      try {
        await Utils.fetchJSON(window.theme.routes.cart_change_url + '.js', {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        await this.updateCartUI();
      } catch (error) {
        console.error('Error updating cart:', error);
        this.showToast('Unable to update cart. Please try again.');
      } finally {
        page.classList.remove('is-loading');
      }
    },

    _isUpdating: false,
    _updateQueued: false,
    async updateCartUI() {
      // Coalesce rather than drop: a refresh requested mid-refresh re-runs once at the end
      if (this._isUpdating) {
        this._updateQueued = true;
        return;
      }
      this._isUpdating = true;
      try {
        const cart = await Utils.fetchJSON(window.theme.routes.cart_url + '.js');
        for (const el of document.querySelectorAll('[data-cart-count]')) {
          el.textContent = cart.item_count;
          el.classList.toggle('hidden', cart.item_count === 0);
        }

        // Refresh cart drawer content
        if (this.drawer) {
          const drawerContent = await Utils.fetchHTML(window.location.pathname + '?section_id=cart-drawer');
          const parser = new DOMParser();
          const doc = parser.parseFromString(drawerContent, 'text/html');
          const newDrawerInner = doc.querySelector('[data-cart-drawer-inner]');
          const currentDrawerInner = this.drawer.querySelector('[data-cart-drawer-inner]');
          if (newDrawerInner && currentDrawerInner) {
            currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
          }
        }

        // Refresh main cart page content
        const cartPage = document.querySelector('[data-cart-page]');
        if (cartPage) {
          const pageContent = await Utils.fetchHTML(window.theme.routes.cart_url + '?section_id=main-cart');
          const parser = new DOMParser();
          const doc = parser.parseFromString(pageContent, 'text/html');
          const newCartPage = doc.querySelector('[data-cart-page]');
          if (newCartPage) {
            cartPage.innerHTML = newCartPage.innerHTML;
          }
          // Announce to screen readers
          const liveRegion = document.getElementById('cart-live-region');
          if (liveRegion) {
            liveRegion.textContent = cart.item_count > 0
              ? `Cart updated. ${cart.item_count} item${cart.item_count !== 1 ? 's' : ''} in your cart.`
              : 'Your cart is now empty.';
          }
        }

        this.loadUpsells(cart);
      } catch (error) {
        console.error('Error updating cart:', error);
      } finally {
        this._isUpdating = false;
        if (this._updateQueued) {
          this._updateQueued = false;
          this.updateCartUI();
        }
      }
    },

    async loadUpsells(cartData) {
      const container = this.drawer?.querySelector('[data-cart-upsell-products]');
      if (!container) return;

      // Merchant-curated metafield upsells are server-rendered — never overwrite them
      const upsellWrapper = container.closest('[data-cart-upsell]');
      if (upsellWrapper?.dataset.hasMetafieldUpsells === 'true') return;

      try {
        // Use passed cart data or fetch if called without (e.g. on drawer open)
        const cart = cartData || await Utils.fetchJSON(window.theme.routes.cart_url + '.js');
        const cartProductIds = new Set(cart.items.map(item => item.product_id));

        if (cart.items.length === 0) return;

        // Use the first cart item's product ID for recommendations
        const sourceProductId = cart.items[0].product_id;
        const limit = 10; // fetch extra to filter cart items

        const data = await Utils.fetchJSON(
          `/recommendations/products.json?product_id=${sourceProductId}&limit=${limit}`
        );

        if (!data.products || data.products.length === 0) {
          container.innerHTML = '';
          return;
        }

        // Filter out products already in cart and limit
        const maxItems = parseInt(container.closest('[data-cart-upsell]')?.dataset.upsellCount, 10) || 2;
        const products = data.products
          .filter(p => !cartProductIds.has(p.id) && p.available)
          .slice(0, maxItems);

        if (products.length === 0) {
          if (upsellWrapper) upsellWrapper.style.display = 'none';
          return;
        }

        if (upsellWrapper) upsellWrapper.style.display = '';
        container.innerHTML = products.map(product => {
          const variant = product.variants[0];
          const imgSrc = product.featured_image
            ? product.featured_image + (product.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=200'
            : '';
          const price = Utils.formatMoney(variant.price);
          const title = Utils.escapeHtml(product.title);
          const titleAttr = Utils.escapeAttr(product.title);
          const urlAttr = Utils.escapeAttr(product.url);
          // One-click add only when there is nothing to choose; multi-variant products
          // link to their page so the shopper picks the right option
          const action = product.variants.length === 1
            ? `<button type="button" class="upsell-item__add" data-upsell-add="${variant.id}">+ Add</button>`
            : `<a href="${urlAttr}" class="upsell-item__add">${Utils.escapeHtml(window.theme?.strings?.viewDetails || 'View options')}</a>`;

          return `<div class="upsell-item">
            <a href="${urlAttr}" class="upsell-item__image" tabindex="-1" aria-hidden="true">
              ${imgSrc ? `<img src="${Utils.escapeAttr(imgSrc)}" alt="${titleAttr}" loading="lazy" width="120" height="120">` : ''}
            </a>
            <div class="upsell-item__info">
              <p class="upsell-item__title"><a href="${urlAttr}">${title}</a></p>
              <p class="upsell-item__price">${price}</p>
            </div>
            ${action}
          </div>`;
        }).join('');
      } catch (error) {
        console.error('Error loading upsells:', error);
        container.innerHTML = '';
      }
    }
  };

  SugarFur.Cart = Cart;

  // Product Form
  const ProductForm = {
    init() {
      for (const form of document.querySelectorAll('[data-product-form], #product-form, form[action*="/cart/add"]')) {
        // Skip forms that have their own dedicated handler (e.g. main product page)
        if (form.hasAttribute('data-product-page-form')) continue;
        this.initForm(form);
      }
    },

    initForm(form) {
      for (const btn of form.querySelectorAll('[data-option-value]')) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const optionGroup = btn.closest('[data-option-group]');
          for (const b of optionGroup.querySelectorAll('[data-option-value]')) {
            b.classList.remove('is-selected');
            b.setAttribute('aria-pressed', 'false');
          }
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
          this.updateVariant(form);
        });
      }

      const quantityInput = form.querySelector('[data-quantity-input]');
      for (const btn of form.querySelectorAll('[data-quantity-change]')) {
        btn.addEventListener('click', () => {
          const change = parseInt(btn.dataset.quantityChange);
          const currentValue = parseInt(quantityInput.value) || 1;
          quantityInput.value = Math.max(1, currentValue + change);
        });
      }

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const variantId = form.querySelector('[name="id"]').value;
        const quantity = parseInt(form.querySelector('[name="quantity"]')?.value) || 1;
        const sellingPlan = form.querySelector('[name="selling_plan"]')?.value || '';
        const addToCartBtn = form.querySelector('[data-add-to-cart]');
        addToCartBtn.disabled = true;
        addToCartBtn.innerHTML = window.theme?.strings?.loading || 'Adding...';
        await Cart.addItem(variantId, quantity, sellingPlan);
        addToCartBtn.disabled = false;
        addToCartBtn.innerHTML = window.theme?.strings?.addToCart || 'Add to Cart';
        // Close quick view modal if this form is inside it
        if (form.closest('#QuickViewModal')) {
          QuickView.close();
        }
      });
    },

    updateVariant(form) {
      const productJson = form.querySelector('[data-product-json]');
      if (!productJson) return;
      let product;
      try { product = JSON.parse(productJson.textContent); } catch (e) { return; }
      const selectedOptions = [];
      for (const group of form.querySelectorAll('[data-option-group]')) {
        const selected = group.querySelector('.is-selected');
        if (selected) selectedOptions.push(selected.dataset.optionValue);
      }
      const variant = product.variants.find(v => v.options.every((opt, i) => opt === selectedOptions[i]));
      if (variant) {
        form.querySelector('[name="id"]').value = variant.id;
        // Price elements can live outside the <form> (quick view renders them above it)
        const scope = form.closest('.quick-view') || form.closest('[data-product-form-scope]') || form;
        const priceEl = scope.querySelector('[data-product-price]');
        const comparePriceEl = scope.querySelector('[data-product-compare-price]');
        const savingsEl = scope.querySelector('[data-product-savings]');
        if (priceEl) priceEl.textContent = Utils.formatMoney(variant.price);
        if (comparePriceEl) {
          if (variant.compare_at_price > variant.price) {
            comparePriceEl.textContent = Utils.formatMoney(variant.compare_at_price);
            comparePriceEl.classList.remove('hidden');
            comparePriceEl.style.display = '';
          } else {
            comparePriceEl.classList.add('hidden');
            comparePriceEl.style.display = 'none';
          }
        }
        if (savingsEl) {
          if (variant.compare_at_price > variant.price) {
            const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
            savingsEl.textContent = (window.theme?.strings?.savePercent || 'Save __PERCENT__%').replace('__PERCENT__', pct);
            savingsEl.style.display = '';
          } else {
            savingsEl.style.display = 'none';
          }
        }
        const addToCartBtn = form.querySelector('[data-add-to-cart]');
        if (variant.available) {
          addToCartBtn.disabled = false;
          addToCartBtn.innerHTML = window.theme?.strings?.addToCart || 'Add to Cart';
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.innerHTML = window.theme?.strings?.soldOut || 'Sold Out';
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }
  };

  SugarFur.ProductForm = ProductForm;

  // Search
  const Search = {
    modal: null,
    input: null,

    init() {
      this.modal = document.querySelector('[data-search-modal]');
      this.input = document.querySelector('[data-predictive-search-input]');
      if (!this.modal) return;

      for (const btn of document.querySelectorAll('[data-search-toggle]')) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openModal();
        });
      }

      for (const btn of document.querySelectorAll('[data-search-close]')) {
        btn.addEventListener('click', () => this.closeModal());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) this.closeModal();
      });

      if (this.input) {
        this.input.addEventListener('input', Utils.debounce(() => this.search(this.input.value), 300));

        // Allow form submission
        const form = this.input.closest('form');
        if (form) {
          form.addEventListener('submit', (e) => {
            if (!this.input.value.trim()) {
              e.preventDefault();
            }
          });
        }
      }
    },

    openModal() {
      const header = document.querySelector('[data-header]');
      if (header) this.modal.style.top = header.offsetHeight + 'px';
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      this._releaseFocus = Utils.trapFocus(this.modal, this.input);
      document.body.style.overflow = 'hidden';
    },

    closeModal() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this._releaseFocus) {
        this._releaseFocus();
        this._releaseFocus = null;
      }
    },

    _abortController: null,
    async search(query) {
      const resultsContainer = document.querySelector('[data-predictive-search-results]');
      if (!query || query.length < 2 || !resultsContainer) {
        if (resultsContainer) resultsContainer.innerHTML = '';
        return;
      }
      // Abort the in-flight request so a slow earlier response can't overwrite newer results
      this._abortController?.abort();
      this._abortController = new AbortController();
      try {
        const url = window.theme?.routes?.predictive_search_url || '/search/suggest';
        const response = await fetch(
          `${url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`,
          { signal: this._abortController.signal }
        );
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const results = html.querySelector('#shopify-section-predictive-search');
        if (results) {
          resultsContainer.innerHTML = results.innerHTML;
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Search error:', error);
      }
    }
  };

  SugarFur.Search = Search;

  // Quick View Modal
  const QuickView = {
    modal: null,

    overlay: null,

    init() {
      this.modal = document.querySelector('#QuickViewModal');
      if (!this.modal) return;

      // Quick view buttons
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-view]');
        if (btn) {
          e.preventDefault();
          const handle = btn.dataset.productHandle;
          if (handle) this.open(handle);
        }
      });

      // Close modal
      for (const btn of this.modal.querySelectorAll('[data-modal-close]')) {
        btn.addEventListener('click', () => this.close());
      }

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
          this.close();
        }
      });

      // Tooltip event delegation (works for dynamically loaded quick view content)
      this.overlay = document.createElement('div');
      this.overlay.className = 'qv-tooltip-overlay';
      document.body.appendChild(this.overlay);
      this.overlay.addEventListener('click', () => this.closeTooltips());

      this.modal.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-qv-tooltip-trigger]');
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          const wrapper = trigger.closest('[data-qv-tooltip]');
          const tooltip = wrapper ? wrapper.querySelector('[data-qv-tooltip-content]') : null;
          if (!tooltip) return;
          const wasOpen = tooltip.classList.contains('is-visible');
          this.closeTooltips();
          if (!wasOpen) {
            tooltip.classList.add('is-visible');
            this.overlay.classList.add('is-visible');
          }
          return;
        }
        const closeBtn = e.target.closest('[data-qv-tooltip-close]');
        if (closeBtn) {
          e.preventDefault();
          this.closeTooltips();
        }
      });
    },

    closeTooltips() {
      if (!this.modal) return;
      this.modal.querySelectorAll('.qv-tooltip.is-visible').forEach(t => t.classList.remove('is-visible'));
      if (this.overlay) this.overlay.classList.remove('is-visible');
    },

    async open(handle) {
      const body = this.modal.querySelector('.modal__body');
      if (!body) return;

      this.modal.classList.add('is-open', 'is-loading');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this._releaseFocus = Utils.trapFocus(this.modal, this.modal.querySelector('[data-modal-close]'));

      body.innerHTML = '<div class="quick-view__loading"><div class="spinner"></div></div>';

      try {
        const response = await fetch(`/products/${handle}?section_id=quick-view-content`);
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const content = html.querySelector('#shopify-section-quick-view-content');

        if (content) {
          body.innerHTML = content.innerHTML;
          // Re-initialize product form for the quick view
          const form = body.querySelector('form[action*="/cart/add"]');
          if (form) ProductForm.initForm(form);
          // Bind gallery thumbnail swapping for the injected content
          const gallery = body.querySelector('[data-product-gallery]');
          if (gallery) ProductGallery.bindGallery(gallery);
          // Re-init Judge.me preview badge for the injected content (best-effort)
          if (window.jdgm) {
            if (typeof window.jdgm.loadPreviewBadges === 'function') {
              try { window.jdgm.loadPreviewBadges(); } catch (e) {}
            } else if (typeof window.jdgm.customizeBadges === 'function') {
              try { window.jdgm.customizeBadges(); } catch (e) {}
            }
          }
        } else {
          // Fallback: redirect to product page
          window.location.href = `/products/${handle}`;
        }
      } catch (error) {
        console.error('Quick view error:', error);
        body.innerHTML = '<p class="quick-view__error">Unable to load product. Please try again.</p>';
      }

      this.modal.classList.remove('is-loading');
    },

    close() {
      this.closeTooltips();
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this._releaseFocus) {
        this._releaseFocus();
        this._releaseFocus = null;
      }
    }
  };

  SugarFur.QuickView = QuickView;

  // Mobile Drawer
  const MobileDrawer = {
    drawer: null,

    init() {
      this.drawer = document.querySelector('[data-mobile-drawer]');
      if (!this.drawer) return;

      for (const btn of document.querySelectorAll('[data-drawer-toggle="mobile-drawer"]')) {
        btn.addEventListener('click', () => this.toggle());
      }

      for (const btn of this.drawer.querySelectorAll('[data-drawer-close]')) {
        btn.addEventListener('click', () => this.close());
      }

      document.querySelector('[data-overlay]')?.addEventListener('click', () => {
        if (this.drawer?.classList.contains('is-open')) this.close();
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
          this.close();
        }
      });

      // Mobile submenu toggles
      for (const btn of this.drawer.querySelectorAll('[data-mobile-menu-toggle]')) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const index = btn.dataset.mobileMenuToggle;
          const submenu = this.drawer.querySelector(`[data-mobile-submenu="${index}"]`);
          if (submenu) {
            const isOpen = submenu.classList.contains('is-open');
            for (const sm of this.drawer.querySelectorAll('[data-mobile-submenu].is-open')) {
              sm.classList.remove('is-open');
            }
            for (const t of this.drawer.querySelectorAll('[data-mobile-menu-toggle]')) {
              t.setAttribute('aria-expanded', 'false');
            }
            if (!isOpen) {
              submenu.classList.add('is-open');
              btn.setAttribute('aria-expanded', 'true');
            }
          }
        });
      }
    },

    toggle() {
      if (this.drawer.classList.contains('is-open')) {
        this.close();
      } else {
        this.open();
      }
    },

    open() {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      for (const btn of document.querySelectorAll('[data-drawer-toggle="mobile-drawer"]')) {
        btn.setAttribute('aria-expanded', 'true');
      }
      document.querySelector('[data-overlay]')?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      this._releaseFocus = Utils.trapFocus(this.drawer, this.drawer.querySelector('[data-drawer-close]'));
    },

    close() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      for (const btn of document.querySelectorAll('[data-drawer-toggle="mobile-drawer"]')) {
        btn.setAttribute('aria-expanded', 'false');
      }
      document.querySelector('[data-overlay]')?.classList.remove('is-visible');
      document.body.style.overflow = '';
      if (this._releaseFocus) {
        this._releaseFocus();
        this._releaseFocus = null;
      }
    }
  };

  SugarFur.MobileDrawer = MobileDrawer;

  // Animations
  const Animations = {
    init() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const animatedElements = document.querySelectorAll('.slide-up, .fade-in');
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          }
        }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
        for (const el of animatedElements) {
          observer.observe(el);
        }
      } else {
        for (const el of animatedElements) {
          el.classList.add('is-visible');
        }
      }
    }
  };

  SugarFur.Animations = Animations;

  // Product Gallery
  const ProductGallery = {
    bindGallery(gallery) {
      if (!gallery || gallery.dataset.galleryBound === '1') return;
      const wrapper = gallery.querySelector('[data-main-media-wrapper]');
      const thumbnails = Array.from(gallery.querySelectorAll('[data-thumbnail]'));
      const getMainImage = () => gallery.querySelector('[data-main-image]');

      const activateThumb = (thumb) => {
        if (!thumb) return;
        // Let any existing media-type-aware click handler (e.g. PDP video swap)
        // fire first; only run our image-src swap if thumb is an image thumb
        // and no other handler is responsible.
        const newSrc = thumb.dataset.fullSrc;
        const newSrcset = thumb.dataset.srcset;
        const mediaType = thumb.dataset.mediaType;
        const mainImage = getMainImage();
        if (mainImage && mainImage.tagName === 'IMG' && (!mediaType || mediaType === 'image')) {
          if (newSrc) mainImage.src = newSrc;
          if (newSrcset) mainImage.srcset = newSrcset;
        }
        for (const t of thumbnails) t.classList.remove('is-active');
        thumb.classList.add('is-active');
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      };

      for (const thumb of thumbnails) {
        thumb.addEventListener('click', () => activateThumb(thumb));
      }

      const currentIndex = () => {
        const i = thumbnails.findIndex((t) => t.classList.contains('is-active'));
        return i === -1 ? 0 : i;
      };
      const step = (dir) => {
        if (thumbnails.length < 2) return;
        const next = (currentIndex() + dir + thumbnails.length) % thumbnails.length;
        const nextThumb = thumbnails[next];
        // Fire the thumb's own click so media-type-specific handlers run too
        nextThumb.click();
      };

      if (wrapper && thumbnails.length > 1) {
        const prevSvg = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
        const nextSvg = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
        const makeArrow = (dir, label, svg) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `gallery-nav gallery-nav--${dir}`;
          btn.setAttribute('aria-label', label);
          btn.dataset.galleryNav = dir;
          btn.innerHTML = svg;
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            step(dir === 'prev' ? -1 : 1);
          });
          return btn;
        };
        const injectArrows = () => {
          if (!wrapper.querySelector('.gallery-nav--prev')) {
            wrapper.appendChild(makeArrow('prev', 'Previous image', prevSvg));
          }
          if (!wrapper.querySelector('.gallery-nav--next')) {
            wrapper.appendChild(makeArrow('next', 'Next image', nextSvg));
          }
        };

        wrapper.classList.add('has-gallery-nav');
        injectArrows();

        // Re-inject arrows if something rewrites wrapper innerHTML (e.g. PDP video swap)
        const observer = new MutationObserver(() => {
          if (!wrapper.querySelector('.gallery-nav')) injectArrows();
        });
        observer.observe(wrapper, { childList: true });

        // Swipe navigation (bound to wrapper — survives innerHTML changes)
        let touchStartX = 0;
        let touchStartY = 0;
        let touchActive = false;
        wrapper.addEventListener('touchstart', (e) => {
          if (!e.touches[0]) return;
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
          touchActive = true;
        }, { passive: true });
        wrapper.addEventListener('touchend', (e) => {
          if (!touchActive || !e.changedTouches[0]) return;
          touchActive = false;
          const dx = e.changedTouches[0].clientX - touchStartX;
          const dy = e.changedTouches[0].clientY - touchStartY;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            step(dx < 0 ? 1 : -1);
          }
        }, { passive: true });

        // Keyboard support when wrapper focused
        wrapper.setAttribute('tabindex', '0');
        wrapper.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        });
      }

      gallery.dataset.galleryBound = '1';
    },
    init() {
      for (const gallery of document.querySelectorAll('[data-product-gallery]')) {
        this.bindGallery(gallery);
      }
    }
  };

  SugarFur.ProductGallery = ProductGallery;

  // Initialize
  function init() {
    Header.init();
    Cart.init();
    ProductForm.init();
    Search.init();
    QuickView.init();
    MobileDrawer.init();
    Animations.init();
    ProductGallery.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
